/* V166.1 — Subscription management card.
 *
 * Renders inside the Dashboard "Settings" tab. Reads the user's active
 * Stripe subscription via /api/billing/my-subscription and offers a
 * "Manage subscription" button that opens Stripe's hosted Customer Portal
 * (cancel, change plan, update card, download invoices).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { CreditCard, ExternalLink, Loader2, CheckCircle, AlertTriangle } from "lucide-react";

import { API } from "../../config";
import PowerTrialOfferModal from "./PowerTrialOfferModal";

export default function SubscriptionManagementCard({ t = {} }) {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);
  const [err, setErr] = useState("");
  const [showTrialOffer, setShowTrialOffer] = useState(false);
  const [trialAccepted, setTrialAccepted] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    axios
      .get(`${API}/billing/my-subscription`, { withCredentials: true })
      .then((r) => active && setSub(r.data))
      .catch((e) => {
        if (e?.response?.status === 401) {
          // anonymous — nothing to render
        } else {
          setErr(e?.response?.data?.detail || "load_failed");
        }
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  const openPortal = async () => {
    setOpening(true);
    setErr("");
    try {
      const r = await axios.post(
        `${API}/billing/portal`,
        { return_url: window.location.origin + "/dashboard?tab=settings" },
        { withCredentials: true }
      );
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else {
        setErr("portal_url_missing");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === "no_subscription_yet") {
        setErr(t.sub_none_yet || "Zatiaľ nemáš žiadne predplatné na zrušenie.");
      } else {
        setErr(detail || "portal_failed");
      }
    } finally {
      setOpening(false);
    }
  };

  // V165.3 — Anti-RankPill churn interceptor. When user clicks the
  // "Manage / Cancel" button, first show 3-day Power Trial offer modal.
  // If they decline OR cooldown is active, proceed to Stripe portal as usual.
  const handlePortalClick = () => {
    if (trialAccepted) return openPortal(); // already saw it, go direct
    setShowTrialOffer(true);
  };

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 p-6" data-testid="sub-mgmt-loading">
        <h3 className="font-bold text-lg font-['Chivo'] mb-2 flex items-center gap-2">
          <CreditCard className="w-5 h-5" /> {t.sub_mgmt_title || "Predplatné"}
        </h3>
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!sub?.has_subscription) {
    return (
      <div className="bg-white border border-gray-200 p-6" data-testid="sub-mgmt-none">
        <h3 className="font-bold text-lg font-['Chivo'] mb-2 flex items-center gap-2">
          <CreditCard className="w-5 h-5" /> {t.sub_mgmt_title || "Predplatné"}
        </h3>
        <p className="text-sm text-gray-600 mb-3">
          {t.sub_mgmt_none || "Zatiaľ nemáš žiadne aktívne mesačné predplatné. Keď si predplatíš plán, môžeš ho tu kedykoľvek spravovať alebo zrušiť."}
        </p>
        <a
          href="/#pricing"
          className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black text-sm font-bold"
          data-testid="sub-mgmt-view-plans"
        >
          {t.sub_mgmt_view_plans || "Pozri plány"}
        </a>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 p-6" data-testid="sub-mgmt-active">
      <h3 className="font-bold text-lg font-['Chivo'] mb-4 flex items-center gap-2">
        <CreditCard className="w-5 h-5" /> {t.sub_mgmt_title || "Predplatné"}
      </h3>

      <div className="space-y-3 mb-5">
        {(sub.subscriptions || []).map((s) => {
          const ending = s.cancel_at_period_end;
          const renews = s.current_period_end ? new Date(s.current_period_end * 1000) : null;
          const trialing = s.status === "trialing";
          const pastDue = s.status === "past_due";
          return (
            <div
              key={s.subscription_id}
              className={`border p-4 ${pastDue ? "border-rose-300 bg-rose-50" : ending ? "border-amber-300 bg-amber-50" : "border-emerald-300 bg-emerald-50"}`}
              data-testid={`sub-row-${s.subscription_id}`}
            >
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="text-xs uppercase tracking-widest text-gray-500 font-bold">
                    {s.package_id || "subscription"}
                  </div>
                  <div className="text-xl font-black mt-0.5">
                    {s.unit_amount != null ? `${s.unit_amount.toFixed(2)} ${s.currency?.toUpperCase()}` : ""}
                    {s.interval ? ` / ${s.interval}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {trialing && (
                    <span className="text-xs uppercase tracking-widest font-black bg-blue-500 text-white px-2 py-1">
                      {t.sub_trialing || "Trial"}
                    </span>
                  )}
                  {pastDue && (
                    <span className="text-xs uppercase tracking-widest font-black bg-rose-500 text-white px-2 py-1 inline-flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> {t.sub_past_due || "Platba zlyhala"}
                    </span>
                  )}
                  {ending && !pastDue && (
                    <span className="text-xs uppercase tracking-widest font-black bg-amber-500 text-black px-2 py-1">
                      {t.sub_cancelling || "Končí na konci obdobia"}
                    </span>
                  )}
                  {!ending && !pastDue && !trialing && (
                    <span className="text-xs uppercase tracking-widest font-black bg-emerald-500 text-white px-2 py-1 inline-flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> {t.sub_active || "Aktívne"}
                    </span>
                  )}
                </div>
              </div>
              {renews && (
                <div className="text-xs text-gray-600 mt-2">
                  {ending
                    ? (t.sub_ends_on || "Končí: ") + renews.toLocaleDateString("sk-SK")
                    : (t.sub_renews_on || "Obnovuje sa: ") + renews.toLocaleDateString("sk-SK")}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button
        onClick={handlePortalClick}
        disabled={opening}
        className="inline-flex items-center gap-2 px-4 py-2.5 bg-black hover:bg-gray-800 text-white text-sm font-bold disabled:opacity-60"
        data-testid="sub-mgmt-portal-btn"
      >
        {opening ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
        {t.sub_mgmt_open_portal || "Spravovať / Zrušiť predplatné"}
      </button>
      <p className="text-xs text-gray-500 mt-2">
        {t.sub_mgmt_portal_hint || "Otvorí sa zabezpečené Stripe rozhranie kde môžeš zmeniť plán, zrušiť predplatné, aktualizovať platobnú kartu, alebo stiahnuť faktúry."}
      </p>

      {err && (
        <div className="mt-3 bg-rose-50 border border-rose-200 text-rose-700 text-xs px-3 py-2">
          {err}
        </div>
      )}

      {/* V165.3 — Anti-RankPill Power Trial offer interceptor */}
      <PowerTrialOfferModal
        open={showTrialOffer}
        source="cancel_click"
        planId={sub?.plan_id || null}
        onAccepted={() => setTrialAccepted(true)}
        onDeclined={() => { setShowTrialOffer(false); openPortal(); }}
        onClose={() => setShowTrialOffer(false)}
      />
    </div>
  );
}
